import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Users,
  Package,
  Building2,
  CornerDownLeft,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useAgencyRole } from "@/hooks/useAgencyRole";
import { supabase } from "@/integrations/supabase/client";
import {
  buildHitRoute,
  buildRecentModelRoute,
  getPaletteNavItems,
  type PaletteNavItem,
} from "@/components/palette/paletteRoutes";

interface ModelHit {
  id: string;
  name: string;
  agencyName?: string | null;
  image?: string | null;
}

interface SearchHit {
  id: string;
  type: "model" | "package" | "agency";
  title: string;
  subtitle: string;
  to: string;
  image?: string | null;
}

/**
 * Global ⌘K command palette. The single source of truth for search and
 * navigation across the app. Other UI (e.g. header search pill) opens it
 * by dispatching a `palette:open` window event.
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recentModels, setRecentModels] = useState<ModelHit[]>([]);
  const [results, setResults] = useState<SearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { agencyId } = useAgencyRole();
  const isAgency = !!agencyId;

  // ⌘K / Ctrl+K toggles; external trigger via `palette:open`
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("palette:open", onOpen as EventListener);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("palette:open", onOpen as EventListener);
    };
  }, []);

  // Reset query when closing
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  // Lazy-fetch recent models for the idle state
  useEffect(() => {
    if (!open || recentModels.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const query = isAgency
          ? supabase
              .from("models")
              .select("id, name, profile_image_url, agencies(name)")
              .eq("agency_id", agencyId!)
              .order("updated_at", { ascending: false, nullsFirst: false })
              .limit(20)
          : supabase
              .from("models_public")
              .select("id, name, profile_image_url, agencies(name)")
              .order("updated_at", { ascending: false, nullsFirst: false })
              .limit(20);
        const { data } = await query;
        if (cancelled || !data) return;
        setRecentModels(
          (data as any[]).map((m) => ({
            id: m.id,
            name: m.name ?? "Untitled",
            agencyName: m.agencies?.name ?? null,
            image: m.profile_image_url ?? null,
          })),
        );
      } catch {
        /* swallow - palette stays useful without models */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isAgency, agencyId, recentModels.length]);

  // Debounced live search across models / packages / agencies
  useEffect(() => {
    if (!user) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      const term = `%${q}%`;
      try {
        const [modelsRes, packagesRes, agenciesRes] = await Promise.all([
          isAgency
            ? supabase
                .from("models")
                .select("id, name, profile_image_url, agencies(name)")
                .eq("agency_id", agencyId!)
                .ilike("name", term)
                .limit(6)
            : supabase
                .from("models_public")
                .select("id, name, profile_image_url, agencies(name)")
                .ilike("name", term)
                .limit(6),
          supabase
            .from("packages")
            .select("id, name")
            .eq("user_id", user.id)
            .is("parent_package_id", null)
            .ilike("name", term)
            .limit(5),
          supabase
            .from("agencies_public")
            .select("id, name, logo_url, city")
            .ilike("name", term)
            .limit(5),
        ]);

        const hits: SearchHit[] = [];
        (modelsRes.data as any[] | null)?.forEach((m) => {
          hits.push({
            id: m.id,
            type: "model",
            title: m.name ?? "Untitled",
            subtitle: m.agencies?.name ?? "Model",
            to: buildHitRoute("model", m.id),
            image: m.profile_image_url,
          });
        });
        (packagesRes.data as any[] | null)?.forEach((p) => {
          hits.push({
            id: p.id,
            type: "package",
            title: p.name ?? "Untitled package",
            subtitle: "Package",
            to: buildHitRoute("package", p.id),
          });
        });
        (agenciesRes.data as any[] | null)?.forEach((a) => {
          hits.push({
            id: a.id,
            type: "agency",
            title: a.name ?? "Agency",
            subtitle: a.city ?? "Agency",
            to: buildHitRoute("agency", a.id),
            image: a.logo_url,
          });
        });
        setResults(hits);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 220);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, user, isAgency, agencyId]);

  const navItems = useMemo(() => getPaletteNavItems(isAgency), [isAgency]);

  const grouped = useMemo(() => {
    const out: Record<string, PaletteNavItem[]> = {};
    for (const item of navItems) (out[item.group] ||= []).push(item);
    return out;
  }, [navItems]);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  // Don't mount for logged-out visitors - they have no destinations to navigate
  if (!user) return null;

  const typeMeta = {
    model: { icon: Users, label: "Model" },
    package: { icon: Package, label: "Package" },
    agency: { icon: Building2, label: "Agency" },
  } as const;

  const showingResults = query.trim().length > 0;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search models, packages, agencies, pages…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>
          {searching ? "Searching…" : "No results found."}
        </CommandEmpty>

        {showingResults && results.length > 0 && (
          <CommandGroup heading="Results">
            {results.map((r) => {
              const Meta = typeMeta[r.type];
              const Icon = Meta.icon;
              return (
                <CommandItem
                  key={`${r.type}-${r.id}`}
                  value={`${r.title} ${r.subtitle} ${r.type}`}
                  onSelect={() => go(r.to)}
                  className="gap-3"
                >
                  {r.image ? (
                    <img
                      src={r.image}
                      alt=""
                      className="h-7 w-7 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="h-7 w-7 rounded-full border border-border/70 flex items-center justify-center shrink-0">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{r.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{r.subtitle}</p>
                  </div>
                  <span className="ml-auto text-[10px] uppercase tracking-[0.14em] text-muted-foreground/60 shrink-0">
                    {Meta.label}
                  </span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {!showingResults &&
          Object.entries(grouped).map(([group, items]) => (
            <CommandGroup key={group} heading={group}>
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem
                    key={item.to}
                    value={`${item.label} ${item.keywords ?? ""}`}
                    onSelect={() => go(item.to)}
                    className="gap-3"
                  >
                    <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-sm text-foreground">{item.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          ))}

        {!showingResults && recentModels.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Recent models">
              {recentModels.slice(0, 8).map((m) => (
                <CommandItem
                  key={m.id}
                  value={`${m.name} ${m.agencyName ?? ""}`}
                  onSelect={() => go(buildRecentModelRoute(m.id))}
                  className="gap-3"
                >
                  {m.image ? (
                    <img src={m.image} alt="" className="h-7 w-7 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="h-7 w-7 rounded-full border border-border/70 flex items-center justify-center shrink-0">
                      <Users className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  )}
                  <span className="text-sm text-foreground truncate">{m.name}</span>
                  {m.agencyName && (
                    <span className="ml-auto text-xs text-muted-foreground truncate">{m.agencyName}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>

      <div className="flex items-center justify-between gap-3 border-t border-border/60 px-5 py-2.5 text-[11px] text-muted-foreground/80">
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1.5">
            <CornerDownLeft className="h-3 w-3" /> open
          </span>
          <span className="inline-flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded border border-border/60 bg-background text-[10px] font-medium">esc</kbd>
            close
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5">
          <kbd className="px-1.5 py-0.5 rounded border border-border/60 bg-background text-[10px] font-medium">⌘</kbd>
          <kbd className="px-1.5 py-0.5 rounded border border-border/60 bg-background text-[10px] font-medium">K</kbd>
          toggle
        </span>
      </div>
    </CommandDialog>
  );
}
